import { useCallback, useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import Cookies from "js-cookie";
import api, { getFreshAccessToken } from "../../chat/api";
import { emitPlanLimit } from "../../../events/planLimitEvents";

/* Socket layer for the workspace copilot.
 *
 * Kept apart from the view so the UI stays declarative and the reconnect /
 * frame-handling logic is testable on its own. Mirrors the proposal copilot's
 * protocol — same frame names — with one addition: `project_focus`, which the
 * server emits when the agent switches project mid-conversation so the header
 * can follow. The user cannot see project ids, so if the agent moves and the UI
 * doesn't, every later answer looks wrong.
 */

const WS_BASE_URL =
  process.env.REACT_APP_WS_URL || "ws://localhost:8000";

const MAX_RECONNECTS = 4;

/* Card turns are stored in Chat rows as marker-prefixed JSON, because the Chat
 * table only holds text. The marker lets both restore paths (tab resume and
 * opening a chat from history) rebuild the CARD instead of printing a blob of
 * JSON at the user. */
export const QUALIFY_MARKER = "QUALIFY_CARD::";
export const RECALL_MARKER = "RECALL_CARD::";
/* Written by the consumer when a project is created (see PROJECT_CARD_MARKER in
 * rfp/websocket/workspace.py). Restored into the SAME shape the live
 * approval frame produces, so the card renders through one code path whether it
 * arrived over the socket a second ago or is being read back from last week. */
export const PROJECT_CARD_MARKER = "PROJECT_CARD::";
export const UPLOAD_CARD_MARKER = "UPLOAD_CARD::";
export const REQUIREMENTS_CARD_MARKER = "REQS_CARD::";
export const PROPOSAL_SETUP_CARD_MARKER = "PROPOSAL_SETUP::";
export const TENDERS_CARD_MARKER = "TENDERS_CARD::";
/* Written ONCE, when Generate is pressed. It records INTENT — that this
 * conversation is writing a proposal for a project — and nothing about
 * progress. How far the work got is the server's to answer, and a snapshot of
 * it here would go stale the moment a section was regenerated elsewhere. */
export const PROPOSAL_FLOW_MARKER = "PROPOSAL_FLOW::";
/* The tool steps of one turn. Written as its own row, immediately before the
 * reply, because that is the order the trace renders in live - so a reopened
 * chat replays without any merging, and no column had to be added to Chat. */
export const TRACE_MARKER = "TRACE::";
/* One completed step of the proposal flow, and the terminal download card.
 *
 * The flow's rows were posted with postLocal only, so they lived in memory and
 * a chat switch wiped the entire thread — twenty minutes of visible history and
 * the download card with it. These two markers make the record durable; the
 * interactive cards in between are not persisted, because a stale outline
 * approval restored as a live card would be answerable twice. */
/* A line the FLOW spoke, as opposed to one the model did.
 *
 * Persisted with a marker for one reason: on restore it has to still be
 * recognisable as narration. Saved as plain text it came back as an ordinary
 * assistant reply, which made it a feedback anchor — so a reloaded run grew a
 * set of copy and thumbs icons after every step instead of one at the end. */
export const NARRATION_MARKER = "SAY::";
export const PROPOSAL_STEP_MARKER = "PROPOSAL_STEP::";
export const PROPOSAL_DONE_MARKER = "PROPOSAL_DONE::";

/* Tools that render their own card. Their steps are kept out of the reasoning
 * trace — the card is a better account of the work than a line describing it. */
const CARD_OWNED_TOOLS = new Set(["create_project", "import_tender", "list_requirements", "generate_proposal"]);

/* Fold each restored trace into the reply it belongs to.
 *
 * A trace is saved as its own row because that is the order it was written in,
 * but it is not its own MESSAGE — live, it renders inside the assistant's block,
 * between the avatar and the text. Left as a standalone row it appeared above
 * the "Sensa" header, unattributed and detached, so a reopened chat looked
 * nothing like the conversation that was had.
 *
 * Merged rather than rendered separately, so there is one way a trace is drawn.
 */
export const mergeTraceRows = (msgs) => {
  const out = [];
  for (const m of msgs) {
    if (m.role === "trace") {
      // Held back; attached to the next assistant reply below.
      out.push({ ...m, _pendingTrace: true });
      continue;
    }
    const prev = out[out.length - 1];
    if (m.role === "assistant" && prev?._pendingTrace) {
      out.pop();
      out.push({ ...m, trace: { steps: prev.steps || [] } });
      continue;
    }
    out.push(m);
  }
  // A trace with no reply after it (a card-only turn) still deserves to show
  // what was done, so it stays as its own row rather than being dropped.
  return out.map(({ _pendingTrace, ...m }) => m);
};

/* Tender Search results. Kept as the rows the search returned at the time —
 * a list of what matched when you asked — while the panel each row opens always
 * fetches the tender as it is now. */
const tendersMessage = (id, c) => ({
  id,
  role: "tenders",
  query: c.query || "",
  matchedBy: c.matched_by,
  total: c.total || 0,
  closedOnly: !!c.closed_only,
  tenders: c.tenders || [],
  // "recommend": ranked from the workspace's Memory rather than a typed topic.
  mode: c.mode || "search",
  weakOnly: !!c.weak_only,
  themes: c.themes || [],
  checked: c.checked || 0,
  // Recommendations by line of work: { label, proof, documented, tenders }.
  groups: c.groups || [],
  emptyThemes: c.empty_themes || [],
  // Whether the shortlist was read against Memory (stage two), and how many
  // tenders that reading ruled out.
  // Undefined on cards saved before the reading existed: they claim nothing.
  judged: c.judged === undefined ? undefined : !!c.judged,
  ruledOutCount: c.ruled_out_count || 0,
  // What a topic search was narrowed to: { states, closing_within_days, stage… }.
  filters: c.filters || {},
  // Classification codes that added tenders: [{ code, title }].
  codes: c.codes || [],
});

export const rowToMessage = (row, i) => {
  const id = (row.django_message_id ?? i) + (row.is_client ? "-u" : "-a");
  const text = row.message || "";
  if (!row.is_client && text.startsWith(PROJECT_CARD_MARKER)) {
    try {
      const c = JSON.parse(text.slice(PROJECT_CARD_MARKER.length));
      return {
        id,
        role: "approval",
        // A synthetic action: the card keys off the tool name, and the decision
        // it represents was made and settled long ago.
        actions: [{ id: `${id}-restored`, function: { name: "create_project", arguments: "{}" } }],
        resolved: "approve",
        submitted: { name: c.name, description: c.description, web_links: c.web_links },
        created: {
          projectId: c.project_id,
          projectName: c.name,
          organizationId: c.organization_id,
        },
      };
    } catch {
      /* unparseable marker row — fall through and show it as plain text */
    }
  }
  if (!row.is_client && text.startsWith(NARRATION_MARKER)) {
    return {
      id,
      role: "assistant",
      text: text.slice(NARRATION_MARKER.length),
      // Not `typed`: it already performed itself once, live. This only marks it
      // as the flow's voice so it is not mistaken for an answer worth rating.
      narration: true,
    };
  }

  if (!row.is_client && text.startsWith(PROPOSAL_STEP_MARKER)) {
    try {
      return { id, role: "proposalStep", ...JSON.parse(text.slice(PROPOSAL_STEP_MARKER.length)) };
    } catch {
      /* fall through to plain text */
    }
  }

  if (!row.is_client && text.startsWith(PROPOSAL_DONE_MARKER)) {
    try {
      const c = JSON.parse(text.slice(PROPOSAL_DONE_MARKER.length));
      return { id, role: "proposalCard", kind: "complete", ...c };
    } catch {
      /* fall through to plain text */
    }
  }

  if (!row.is_client && text.startsWith(TRACE_MARKER)) {
    try {
      const t = JSON.parse(text.slice(TRACE_MARKER.length));
      return {
        id,
        role: "trace",
        steps: Array.isArray(t.steps) ? t.steps : [],
      };
    } catch {
      /* fall through to plain text */
    }
  }

  if (!row.is_client && text.startsWith(PROPOSAL_FLOW_MARKER)) {
    try {
      const c = JSON.parse(text.slice(PROPOSAL_FLOW_MARKER.length));
      return {
        id,
        // Not a rendered message — the page reads it to know this chat has a
        // proposal in progress, then asks the server which step to resume at.
        role: "proposalFlow",
        projectId: c.project_id,
        projectName: c.project_name,
      };
    } catch {
      /* fall through to plain text */
    }
  }

  if (!row.is_client && text.startsWith(PROPOSAL_SETUP_CARD_MARKER)) {
    try {
      const c = JSON.parse(text.slice(PROPOSAL_SETUP_CARD_MARKER.length));
      return {
        id,
        role: "proposalSetup",
        projectId: c.project_id,
        projectName: c.project_name,
        prefill: c.prefill || {},
      };
    } catch {
      /* fall through to plain text */
    }
  }

  // The requirements card carries counts only; the texts are fetched by the
  // card itself, so a reopened chat shows what the project holds NOW rather
  // than a copy of what it held when the question was asked.
  if (!row.is_client && text.startsWith(REQUIREMENTS_CARD_MARKER)) {
    try {
      const c = JSON.parse(text.slice(REQUIREMENTS_CARD_MARKER.length));
      return {
        id,
        role: "requirements",
        projectId: c.project_id,
        projectName: c.project_name,
        state: c.state || "ready",
        total: c.total,
        categories: c.categories || {},
        documents: c.documents || [],
      };
    } catch {
      /* fall through to plain text */
    }
  }

  if (!row.is_client && text.startsWith(TENDERS_CARD_MARKER)) {
    try {
      return tendersMessage(id, JSON.parse(text.slice(TENDERS_CARD_MARKER.length)));
    } catch {
      /* fall through to plain text */
    }
  }

  // Historic rows only: the upload card was replaced by the project panel, so
  // these are rendered as the sentence they always meant rather than as raw JSON.
  if (!row.is_client && text.startsWith(UPLOAD_CARD_MARKER)) {
    try {
      const c = JSON.parse(text.slice(UPLOAD_CARD_MARKER.length));
      const n = (c.documents || []).length;
      return {
        id,
        role: "assistant",
        text: n
          ? `Added ${n} document${n === 1 ? "" : "s"} to ${c.project_name || "the project"}.`
          : `Opened ${c.project_name || "the project"}.`,
      };
    } catch {
      /* unparseable marker row — fall through and show it as plain text */
    }
  }
  if (!row.is_client && text.startsWith(RECALL_MARKER)) {
    try {
      const saved = JSON.parse(text.slice(RECALL_MARKER.length));
      return {
        id,
        role: "recall",
        // Restored cards are always complete: nothing is pending, because the
        // enrich pass finished before the row was ever written.
        state: "ready",
        projectLabel: saved.projectLabel,
        projectId: saved.projectId,
        data: saved.data,
      };
    } catch {
      /* unparseable marker row — fall through and show it as plain text */
    }
  }
  if (!row.is_client && text.startsWith(QUALIFY_MARKER)) {
    try {
      const saved = JSON.parse(text.slice(QUALIFY_MARKER.length));
      return {
        id,
        role: "qualify",
        state: "ready",
        projectLabel: saved.projectLabel,
        projectId: saved.projectId,
        data: saved.data,
      };
    } catch {
      /* unparseable marker row — fall through and show it as plain text */
    }
  }
  return {
    id,
    role: row.is_client ? "user" : "assistant",
    text,
    // Feedback is recorded against the persisted row, so the ids travel with
    // the message rather than being looked up again later.
    messageId: row.bot_message_id,
    runId: row.run_id,
    feedback: row.feedback || null,
  };
};

/* The pinned project of a restored chat, derived from the server's row tags.
 * Last tagged row wins: it is the project the conversation was about when it
 * last spoke, which is what the chip should claim on reopen. */
export const projectFromRows = (rows) => {
  for (let i = (rows || []).length - 1; i >= 0; i--) {
    const row = rows[i];
    if (row && row.folder_id) {
      return { id: row.folder_id, label: row.folder_name || `Project ${row.folder_id}` };
    }
  }
  return null;
};

export default function useWorkspaceCopilot(options = {}) {
  // Ref'd so the once-only resume effect never holds a stale callback.
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const [messages, setMessages] = useState([]);
  const [status, setStatus] = useState("idle"); // idle | connecting | ready | error
  const [streaming, setStreaming] = useState(false);
  const [activeTool, setActiveTool] = useState(null);
  // The live reasoning trace: when the turn started, what the agent has done
  // so far, and any reasoning text the deployment chose to stream.
  const [trace, setTrace] = useState(null);
  // True between clicking stop and the server confirming — the button must
  // not look idle while the request is still in flight.
  const [stopping, setStopping] = useState(false);
  // The newest rewrite_section card, consumed once by the page and cleared.
  const [lastRewrite, setLastRewrite] = useState(null);
  // A request to export, consumed once by the page: it opens the design chooser.
  const [lastExport, setLastExport] = useState(null);
  const [focus, setFocus] = useState(null); // { project_id, project_name }

  const [lastClose, setLastClose] = useState(null); // { code, reason, everOpened }

  const wsRef = useRef(null);
  // Leaving the page unmounts this hook, and a remount used to mint a fresh
  // chat id — so navigating to another module and back silently started a new
  // conversation, dropping the transcript AND the pinned project. The active
  // id now lives in sessionStorage (per-tab, dies with the tab): coming back
  // resumes the same conversation; only an explicit "New chat" mints a new one.
  const chatIdRef = useRef(null);
  const resumedRef = useRef(false);
  if (chatIdRef.current === null) {
    let stored = null;
    try {
      stored = sessionStorage.getItem("rfp_copilot_active_chat");
    } catch {
      /* storage unavailable - behave as before */
    }
    resumedRef.current = !!stored;
    chatIdRef.current = stored || uuidv4();
  }
  // Mirrored into state as well as the ref: the ref drives the socket URL, the
  // state lets the history drawer highlight the conversation on screen.
  const [chatId, setChatId] = useState(chatIdRef.current);
  const queueRef = useRef([]);
  const retriesRef = useRef(0);
  const connectingRef = useRef(false);
  // Bumped every time the hook is pointed at a different chat_id. The chat_id is
  // in the socket URL, so switching conversations means tearing one socket down
  // and building another — and a connect started before the switch must not be
  // allowed to finish and claim wsRef, or the two fight over the same handlers.
  const genRef = useRef(0);
  // Whether this hook has ever completed a handshake. A socket that closes
  // without ever opening is a server-side problem (route not deployed, auth
  // rejected) — very different from a live connection dropping, and the two
  // need different messages or you debug the wrong end for an hour.
  const everOpenedRef = useRef(false);
  // The assistant turn being streamed. Held in a ref so each delta appends
  // without the closure capturing a stale message list.
  const liveIdRef = useRef(null);
  // Approval cards already answered. Only grows within a conversation, and it
  // is reset on rebind along with the transcript it describes.
  const answeredApprovalsRef = useRef(new Set());

  /* Add streamed text to the reply being written.
   *
   * Found BY ID, anywhere in the list — not by assuming it is the last entry.
   * A card or flow row can be posted while the answer is still streaming, and
   * the moment one lands behind it the tail check fails and the rest of the
   * sentence opens a second reply under its own Sensa header. */
  const appendDelta = useCallback((text) => {
    setMessages((prev) => {
      const id = liveIdRef.current;
      const idx = id ? prev.findIndex((m) => m.id === id) : -1;
      if (idx < 0) return [...prev, { id, role: "assistant", text }];
      const next = [...prev];
      next[idx] = { ...next[idx], text: next[idx].text + text };
      return next;
    });
  }, []);

  const handleFrame = useCallback(
    (data) => {
      switch (data.type) {
        case "reasoning": {
          const text = data.message || "";
          if (!text) break;

          // `reclassify` means this text already streamed into the visible reply
          // as progress deltas, and the server has since decided it was narration
          // before a tool call. Strip that exact span back off the live message —
          // suffix match, so a partial or duplicated frame can never eat the
          // wrong text.
          if (data.reclassify) {
            const liveId = liveIdRef.current;
            setMessages((prev) => {
              const idx = prev.findIndex((m) => m.id === liveId);
              if (idx < 0) return prev;
              const msg = prev[idx];
              if (!msg.text.endsWith(text)) return prev;
              const rest = msg.text.slice(0, msg.text.length - text.length);

              /* ONLY here is the live id released. It used to be released
               * whenever anything was stripped at all, including when the
               * message survived with its opening words intact — and then the
               * next delta had nowhere to land and started a SECOND reply.
               *
               * Released inside the updater, not from a flag read after it:
               * React 18 batches, so an updater may not have run by the time
               * the following line executes. */
              if (!rest.trim()) {
                liveIdRef.current = null;
                return [...prev.slice(0, idx), ...prev.slice(idx + 1)];
              }
              return prev.map((m, i) => (i === idx ? { ...m, text: rest } : m));
            });
          }

          setTrace((t) => {
            const base = t || { startedAt: Date.now(), steps: [] };
            const prior = base.thought || "";
            return { ...base, thought: prior ? `${prior}

${text}` : text };
          });
          break;
        }
        case "progress":
          if (!liveIdRef.current) liveIdRef.current = uuidv4();
          setStreaming(true);
          appendDelta(data.message || "");
          break;
        case "tool_starting":
          setActiveTool(data.name);
          // Card-owned tools are deliberately absent from the trace. The card
          // already narrates its own progress, and listing the step as well
          // prints "Creating the project — created 'vapi'" directly beneath a
          // card that says vapi ✓ Created. Same fact, three times on screen.
          if (!CARD_OWNED_TOOLS.has(data.name)) {
            setTrace((t) => {
              const base = t || { startedAt: Date.now(), steps: [] };
              return {
                ...base,
                steps: [
                  ...base.steps,
                  { id: data.tool_call_id, name: data.name, detail: data.detail || "", status: "running" },
                ],
              };
            });
          }
          break;
        case "tool_completed":
          setActiveTool(null);
          setTrace((t) =>
            t
              ? {
                  ...t,
                  steps: t.steps.map((s) =>
                    s.id === data.tool_call_id
                      ? {
                          ...s,
                          status: data.ok ? "done" : "failed",
                          detail: data.detail || s.detail,
                          // The passages this search found, carried so a [n] in
                          // the reply has something to resolve against. They
                          // arrive BEFORE the text that cites them — the model
                          // searches, then writes — so a citation is clickable
                          // from the moment it streams in.
                          ...(data.sources?.length ? { sources: data.sources } : null),
                        }
                      : s
                  ),
                }
              : t
          );
          // Settle the card that asked for this, matched on tool_call_id — the
          // same id the approval frame carried. Correlating any other way
          // (newest card, latest focus change) breaks the moment two things are
          // in flight. Both outcomes land here: the card cannot know whether it
          // worked until this frame says so.
          // The copilot asking to add documents IS the request to open the
          // project panel — that is where the drop zone lives now. Fired from
          // the completed frame so the panel opens already knowing what is in
          // the project rather than flashing an empty list.
          if (data.name === "upload_documents" && data.ok && data.project_id) {
            optionsRef.current?.onOpenProject?.({
              projectId: data.project_id,
              projectName: data.project_name,
            });
          }
          if (CARD_OWNED_TOOLS.has(data.name)) {
            setMessages((prev) =>
              prev.map((m) => {
                if (m.role !== "approval") return m;
                if (!m.actions?.some((a) => a?.id === data.tool_call_id)) return m;
                if (!data.ok) {
                  return {
                    ...m,
                    failed:
                      data.detail ||
                      (data.name === "upload_documents"
                        ? "The documents couldn't be added."
                        : "It couldn't be created."),
                  };
                }
                if ((data.name === "create_project" || data.name === "import_tender") && data.project_id) {
                  return {
                    ...m,
                    created: {
                      projectId: data.project_id,
                      projectName: data.project_name,
                      organizationId: data.organization_id,
                    },
                  };
                }
                return m;
              })
            );
          }
          break;
        case "approval_required": {
          // The turn is now parked on the server until we answer. Streaming has
          // to be released here — the server sends no `completed` for a paused
          // turn, so leaving it true would freeze the composer permanently.
          const liveId = liveIdRef.current;
          setTrace((t) => {
            if (t && liveId) {
              const elapsedMs = Date.now() - t.startedAt;
              setMessages((prev) =>
                prev.map((m) => (m.id === liveId ? { ...m, trace: { ...t, elapsedMs } } : m))
              );
            }
            return null;
          });
          liveIdRef.current = null;
          setStreaming(false);
          setActiveTool(null);
          setStopping(false);
          setMessages((prev) => [
            ...prev,
            {
              id: uuidv4(),
              role: "approval",
              actions: data.actions || [],
              // Only the newest card is answerable: the server keeps exactly one
              // pending set, so an older card's buttons would resolve whatever is
              // pending now — a click meaning something the user never read.
              resolved: null,
            },
          ]);
          break;
        }
        case "card": {
          // Arrives the moment the tool returns, so the card sits ABOVE the
          // sentence written about it — the order a reopened chat replays.
          const c = data.card || {};
          if (c.kind === "proposal_setup") {
            setMessages((prev) => [
              // Retire every earlier setup card. Two live cards each with a
              // Generate button means one of them saves values the user has
              // already changed away from.
              ...prev.map((m) =>
                m.role === "proposalSetup" ? { ...m, superseded: true } : m
              ),
              {
                id: uuidv4(),
                role: "proposalSetup",
                projectId: c.project_id,
                projectName: c.project_name,
                prefill: c.prefill || {},
              },
            ]);
          }
          /* Not a message row: this card does not render, it DRIVES the
           * proposal flow on the page. Pushing it into the transcript would
           * leave a row with no renderer, and replaying an old chat would
           * re-trigger a redraft of a section somebody finished weeks ago. */
          if (c.kind === "rewrite_section" || c.kind === "write_section") {
            setLastRewrite({
              id: uuidv4(),
              projectId: c.project_id,
              // Needed when this card STARTS a flow rather than continuing one:
              // there is no earlier state to take the project's name from.
              projectName: c.project_name || "",
              sectionIndex: c.section_index,
              sectionLabel: c.section_label || "",
              instruction: c.instruction || "",
              // "continue" writes the next section for the first time; "rewrite"
              // replaces one that exists. Only the wording differs, but a row
              // that says "Rewriting" about a blank section is a lie.
              mode: c.mode || "rewrite",
            });
          }
          if (c.kind === "proposal_export") {
            setLastExport({
              id: uuidv4(),
              projectId: c.project_id,
              projectName: c.project_name || "",
              // Whether the proposal is actually finished. Decides what the
              // export card claims, and whether it belongs in the transcript.
              partial: !!c.partial,
              sectionsWritten: c.sections_written || 0,
              sectionsTotal: c.sections_total || 0,
            });
          }
          if (c.kind === "tenders") {
            setMessages((prev) => [...prev, tendersMessage(uuidv4(), c)]);
          }
          if (c.kind === "requirements") {
            setMessages((prev) => [
              ...prev,
              {
                id: uuidv4(),
                role: "requirements",
                projectId: c.project_id,
                projectName: c.project_name,
                state: c.state || "ready",
                total: c.total,
                categories: c.categories || {},
                documents: c.documents || [],
              },
            ]);
          }
          break;
        }
        case "project_focus":
          setFocus(
            data.project_id
              ? { project_id: data.project_id, project_name: data.project_name }
              : null
          );
          break;
        case "stopped": {
          // The server confirmed it stopped. Whatever streamed stays on screen —
          // the user watched it arrive, and clearing it would make the button
          // look like it deleted their answer instead of ending it.
          const liveId = liveIdRef.current;
          setTrace((t) => {
            if (t && liveId) {
              const elapsedMs = Date.now() - t.startedAt;
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === liveId ? { ...m, trace: { ...t, elapsedMs }, stopped: true } : m
                )
              );
            }
            return null;
          });
          liveIdRef.current = null;
          setStreaming(false);
          setActiveTool(null);
          setStopping(false);
          break;
        }
        case "completed": {
          // Park the finished trace on the message it produced, so it collapses
          // to "Thought for 6s" above that reply and stays inspectable later —
          // rather than vanishing the moment the answer arrives.
          const liveId = liveIdRef.current;
          setTrace((t) => {
            if (t && liveId) {
              const elapsedMs = Date.now() - t.startedAt;
              setMessages((prev) =>
                prev.map((m) => (m.id === liveId ? { ...m, trace: { ...t, elapsedMs } } : m))
              );
            }
            return null;
          });
          // The ids the server persisted this reply under. Without them the
          // thumbs would only work on a reopened chat — never on the answer
          // just read, which is when anyone actually rates one.
          if (liveId && data.message_id && data.run_id) {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === liveId ? { ...m, messageId: data.message_id, runId: data.run_id } : m
              )
            );
          }
          liveIdRef.current = null;
          setStreaming(false);
          setActiveTool(null);
          setStopping(false);
          break;
        }
        case "plan_limit":
          // Sensa's create_project hit the plan limit; the agent explains it in the chat.
          emitPlanLimit(data.message, data.limit_kind);
          break;
        case "error":
          if (data.code === "plan_limit") emitPlanLimit(data.message);
          liveIdRef.current = null;
          setStreaming(false);
          setActiveTool(null);
          setTrace(null);
          setStopping(false);
          setMessages((prev) => [
            ...prev,
            { id: uuidv4(), role: "error", text: data.message || "Something went wrong." },
          ]);
          break;
        default:
          if (data.status === "connected") setStatus("ready");
      }
    },
    [appendDelta]
  );

  const connect = useCallback(async () => {
    if (connectingRef.current) return;
    if (wsRef.current?.readyState === WebSocket.OPEN) return;
    connectingRef.current = true;
    setStatus("connecting");

    const gen = genRef.current;
    let token;
    try {
      token = await getFreshAccessToken();
    } catch {
      if (gen === genRef.current) {
        connectingRef.current = false;
        setStatus("error");
      }
      return;
    }
    // The conversation changed while the token was being fetched — this socket
    // would be for the chat the user just left.
    if (gen !== genRef.current) return;

    const ws = new WebSocket(
      `${WS_BASE_URL}/ws/workspace-copilot/${chatIdRef.current}/?token=${token}`
    );
    wsRef.current = ws;
    // Every handler below is a no-op once the socket has been superseded, so a
    // late close from an abandoned conversation can't null out the live ref or
    // restart the reconnect ladder against it.
    const current = () => genRef.current === gen && wsRef.current === ws;

    ws.onopen = () => {
      if (!current()) return;
      connectingRef.current = false;
      retriesRef.current = 0;
      everOpenedRef.current = true;
      setLastClose(null);
      // Anything typed before the socket opened still gets sent, rather than
      // silently vanishing — the first message is often sent immediately.
      while (queueRef.current.length) ws.send(JSON.stringify(queueRef.current.shift()));
    };
    ws.onmessage = (event) => {
      if (!current()) return;
      try {
        handleFrame(JSON.parse(event.data));
      } catch {
        /* ignore malformed frames */
      }
    };
    ws.onerror = () => {
      if (current()) setStatus("error");
    };
    ws.onclose = (event) => {
      if (!current()) return;
      connectingRef.current = false;
      wsRef.current = null;
      setLastClose({
        code: event?.code,
        reason: event?.reason || "",
        everOpened: everOpenedRef.current,
      });
      // Logged, not swallowed: without the close code, "lost connection" is
      // indistinguishable from a missing route or a rejected token.
      console.warn(
        `[workspace-copilot] socket closed code=${event?.code} reason="${event?.reason || ""}" ` +
          `everOpened=${everOpenedRef.current}`
      );
      if (retriesRef.current < MAX_RECONNECTS) {
        const delay = 600 * 2 ** retriesRef.current;
        retriesRef.current += 1;
        setTimeout(connect, delay);
      } else {
        setStatus("error");
      }
    };
  }, [handleFrame]);

  // Record the id this tab is on, and - when the id came from storage - pull
  // the transcript back so "come back to the page" shows the conversation, not
  // a blank greeting over a live history the server still remembers.
  useEffect(() => {
    try {
      sessionStorage.setItem("rfp_copilot_active_chat", chatIdRef.current);
    } catch {
      /* non-fatal */
    }
    if (!resumedRef.current) return undefined;
    resumedRef.current = false;
    let cancelled = false;
    // The chat this restore is FOR. The page can move to another chat while the
    // request is out (opening Sensa from a project page starts that project's
    // chat at once), and the old chat's turns must not land in the new one.
    const askedFor = chatIdRef.current;
    (async () => {
      try {
        /* Scoped to this surface's turns.
         *
         * A chat_id is not unique to one conversation: other chat surfaces
         * write to the same table, and one has been seen adopting a Sensa
         * session's id and writing its own turns under it. Without this the
         * restore replayed that other conversation inside this one. */
        const res = await api.get(
          "/api/v1/copilot/get-chat-sessions/" +
            askedFor +
            "/?chat_module=workspace_copilot"
        );
        if (cancelled || chatIdRef.current !== askedFor) return;
        const restored = mergeTraceRows(
          (res?.data?.chat_history || []).filter((row) => row && row.message).map(rowToMessage)
        );
        // Never clobber a conversation the user already started typing into.
        if (restored.length) setMessages((prev) => (prev.length ? prev : restored));
        optionsRef.current?.onRestoreRows?.(askedFor, res?.data?.chat_history || []);
      } catch {
        /* 404 just means no turns were persisted yet - an empty page is right */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    connect();
    return () => {
      // Stop the reconnect ladder before closing, or unmount triggers a retry.
      retriesRef.current = MAX_RECONNECTS;
      genRef.current += 1;
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [connect]);

  const send = useCallback(
    (text, { projectId } = {}) => {
      const body = String(text || "").trim();
      if (!body) return;
      // Moving on abandons any pending action — the server seals the dangling
      // tool call and drops its pending set when a new question arrives. The
      // card has to say so, or it keeps offering an Approve button that would
      // come back "no pending actions found".
      setMessages((prev) => [
        ...prev.map((m) => {
          if (m.role !== "approval" || m.resolved) return m;
          answeredApprovalsRef.current.add(m.id);
          return { ...m, resolved: "superseded" };
        }),
        { id: uuidv4(), role: "user", text: body },
      ]);
      setStreaming(true);
      setTrace({ startedAt: Date.now(), steps: [] });
      const payload = {
        client_message: body,
        organization_id: Cookies.get("selected_org_id") || undefined,
        ...(projectId !== undefined ? { project_id: projectId } : {}),
      };
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify(payload));
      } else {
        queueRef.current.push(payload);
        connect();
      }
    },
    [connect]
  );

  // Rebind the hook to a different chat_id. Shared by "new chat" (empty
  // transcript, fresh id) and by opening one from history (its id, its
  // transcript). Both need the same thing: a new server-side history bucket,
  // which means the socket is re-established rather than reused — the chat_id
  // is in the URL, so an open socket can't be pointed at another conversation.
  const rebind = useCallback(
    (nextChatId, nextMessages) => {
      genRef.current += 1;
      chatIdRef.current = nextChatId;
      setChatId(nextChatId);
      try {
        sessionStorage.setItem("rfp_copilot_active_chat", nextChatId);
      } catch {
        /* non-fatal */
      }
      liveIdRef.current = null;
      answeredApprovalsRef.current = new Set();
      queueRef.current = [];
      setMessages(nextMessages);
      setFocus(null);
      setStreaming(false);
      setActiveTool(null);
      setTrace(null);

      const stale = wsRef.current;
      wsRef.current = null;
      stale?.close();

      retriesRef.current = 0;
      connectingRef.current = false;
      connect();
    },
    [connect]
  );

  /* Stop the in-flight reply.
   *
   * Fire-and-forget by design: the socket may already be closing, and a stop
   * that cannot be delivered must still free the UI rather than leaving the
   * user stuck watching a button that does nothing.
   */
  const stop = useCallback(() => {
    setStopping(true);
    try {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ action: "stop" }));
      } else {
        // No socket to tell. Nothing is streaming to us either, so release
        // the UI immediately instead of waiting for a frame that cannot come.
        setStreaming(false);
        setActiveTool(null);
        setStopping(false);
      }
    } catch {
      setStreaming(false);
      setStopping(false);
    }
  }, []);

  /* Answer the pending approval. `action` is "approve" or "deny".
   *
   * `toolArgs` is {tool_call_id: {...}} — the values the user actually typed
   * into the card, which the server folds over what the model proposed (on a
   * per-tool allowlist). `submitted` is the same values kept locally, so the
   * resolved card shows what was created rather than what was suggested.
   *
   * Marked resolved the moment it is sent, so the buttons cannot be clicked
   * twice — a second "approve" would find no pending set on the server and come
   * back as an error the user cannot make sense of.
   */
  const respondToApproval = useCallback((id, action, toolArgs, submitted) => {
    // Guarded on a ref, not on message state: both handlers of a double-click
    // run before React re-renders the card into its resolved form, so a state
    // check here still lets the second frame out — and the server, having
    // already consumed its one pending set, answers that with an error.
    if (answeredApprovalsRef.current.has(id)) return;
    if (wsRef.current?.readyState !== WebSocket.OPEN) {
      setMessages((prev) => [
        ...prev,
        {
          id: uuidv4(),
          role: "error",
          text: "Lost connection before that could be confirmed. Ask again once you're back online.",
        },
      ]);
      return;
    }
    answeredApprovalsRef.current.add(id);
    setMessages((prev) =>
      prev.map((m) =>
        m.id === id && !m.resolved ? { ...m, resolved: action, submitted } : m
      )
    );
    wsRef.current.send(
      JSON.stringify({ action, ...(toolArgs ? { tool_args: toolArgs } : {}) })
    );
    setStreaming(true);
    setTrace({ startedAt: Date.now(), steps: [] });
  }, []);

  const reset = useCallback(() => rebind(uuidv4(), []), [rebind]);

  const loadSession = useCallback(
    (id, history) => {
      // The REST shape (is_client / message) differs from the socket's, so it is
      // normalised here rather than teaching the view two vocabularies.
      const restored = mergeTraceRows(
        (history || []).filter((row) => row && row.message).map(rowToMessage)
      );
      rebind(id, restored);
    },
    [rebind]
  );

  // Local messages: cards the PAGE creates (like the /qualify result) rather
  // than turns the agent produced. They live in the same list so they scroll,
  // stack and animate like everything else — but they are never sent anywhere.
  const postLocal = useCallback((msg) => {
    const id = uuidv4();
    setMessages((prev) => [...prev, { id, ...msg }]);
    return id;
  }, []);
  const patchLocal = useCallback((id, patch) => {
    setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, ...patch } : m)));
  }, []);

  return {
    messages,
    status,
    streaming,
    activeTool,
    trace,
    stopping,
    focus,
    lastClose,
    lastRewrite,
    clearLastRewrite: () => setLastRewrite(null),
    lastExport,
    clearLastExport: () => setLastExport(null),
    chatId,
    send,
    stop,
    respondToApproval,
    reset,
    loadSession,
    postLocal,
    patchLocal,
  };
}
